import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip } from '@mui/material';
import { Edit, Delete as DeleteIcon, Block, CheckCircle, InfoOutlined } from '@mui/icons-material';
import Progress from '../Progress';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_API_BASE_URL}/company-master/access-roles`;

function AccessRolesTable({ onEdit, reloadKey }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [infoRole, setInfoRole] = useState(null);
  const navigate = useNavigate();

  const handleApiError = (e, fallbackMessage) => {
    if (e.response && e.response.status === 401) {
      setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
      setTimeout(() => navigate('/'), 1000);
    } else {
      setNotify({ isOpen: true, message: e.response?.data?.message || e.response?.data?.error_message || fallbackMessage, type: 'error' });
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API);
      setRoles(response.data?.data || []);
    } catch (e) {
      handleApiError(e, 'Failed to fetch access roles');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const handleToggleStatus = async (role) => {
    const activate = Number(role.status) !== 1;
    if (!window.confirm(activate ? `Activate role "${role.role_name}"?` : `Deactivate role "${role.role_name}"?`)) return;
    try {
      const response = await axios.post(`${API}/${role.role_id}/status`, { status: activate ? 1 : 0 });
      setNotify({ isOpen: true, message: response.data.message, type: 'success' });
      fetchRoles();
    } catch (e) {
      handleApiError(e, 'Failed to update role status');
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Permanently delete role "${role.role_name}"?`)) return;
    try {
      const response = await axios.delete(`${API}/${role.role_id}`);
      setNotify({ isOpen: true, message: response.data.message, type: 'success' });
      fetchRoles();
    } catch (e) {
      handleApiError(e, 'Failed to delete access role');
    }
  };

  if (loading) return <Progress />;

  return (
    <>
      <div className="table-parent">
        <table className="table big-table">
          <thead className="table-dark">
            <tr>
              <th scope="col" className="table-dark">Actions</th>
              <th scope="col">Role Name</th>
              <th scope="col">Description</th>
              <th scope="col">Permissions</th>
              <th scope="col">Users</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center' }}>No access roles found</td></tr>
            )}
            {roles.map((role) => {
              const isSystem = Number(role.is_system) === 1;
              const isActive = Number(role.status) === 1;
              return (
                <tr key={role.role_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View Permissions">
                      <IconButton size="small" color="info" onClick={() => setInfoRole(role)}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isSystem ? 'Built-in roles cannot be edited' : 'Modify'}>
                      <span>
                        <IconButton size="small" color="primary" disabled={isSystem} onClick={() => onEdit(role)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isSystem ? 'Built-in roles cannot be deactivated' : (isActive ? 'Deactivate' : 'Activate')}>
                      <span>
                        <IconButton size="small" color={isActive ? 'warning' : 'success'} disabled={isSystem} onClick={() => handleToggleStatus(role)}>
                          {isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isSystem ? 'Built-in roles cannot be deleted' : 'Delete'}>
                      <span>
                        <IconButton size="small" color="error" disabled={isSystem} onClick={() => handleDelete(role)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </td>
                  <td>{role.role_name}{isSystem ? ' (Built-in)' : ''}</td>
                  <td>{role.role_description || '-'}</td>
                  <td>{role.permissions?.length || 0}</td>
                  <td>{role.user_count || 0}</td>
                  <td>{isActive ? 'Active' : 'Inactive'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!infoRole} onClose={() => setInfoRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Permissions — {infoRole?.role_name}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {(infoRole?.permissions || []).map((key) => <Chip key={key} label={key} size="small" />)}
            {(infoRole?.permissions || []).length === 0 && 'No permissions selected'}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoRole(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </>
  );
}

export default AccessRolesTable;
